/**
 * 同盾风控脚本延迟加载工具
 * 用于延迟加载同盾脚本，避免影响 TBT（Total Blocking Time）
 */

import { env } from './env';

// 脚本加载状态
let scriptLoadPromise: Promise<string | null> | null = null;
let scriptLoaded = false;
let scriptLoading = false;

/**
 * 初始化同盾脚本（延迟加载）
 * @returns Promise<string | null> 返回 black_box 值
 */
export function initTongDunScript(): Promise<string | null> {
  // 如果已经加载完成，直接返回当前值
  if (scriptLoaded) {
    return Promise.resolve(getTongDunBlackBox());
  }

  // 如果正在加载，返回现有的 Promise
  if (scriptLoading && scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // 开始加载脚本
  scriptLoading = true;
  scriptLoadPromise = new Promise<string | null>((resolve) => {
    if (typeof window === 'undefined') {
      scriptLoading = false;
      resolve(null);
      return;
    }

    // 设置同盾配置
    window._fmOpt = {
      partner: env.NEXT_PUBLIC_TONG_DUN_PARTNER_ID,
      success(data: string) {
        scriptLoaded = true;
        scriptLoading = false;
        resolve(data);
      },
    };

    // 动态加载脚本
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${env.NEXT_PUBLIC_TONG_DUN_BASE_URL}?t=${(new Date().getTime() / 3600000).toFixed(0)}`;

    // 人机验证的脚本和同盾放在一起
    const recaptchaScript = document.createElement('script');
    recaptchaScript.type = 'text/javascript';
    recaptchaScript.async = true;
    recaptchaScript.src = 'https://www.google.com/recaptcha/api.js?render=explicit';

    // 处理加载错误
    script.onerror = () => {
      scriptLoading = false;
      resolve(null);
    };

    // 插入脚本
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
    // 插入 recaptcha 脚本
    document.head.appendChild(recaptchaScript);

    // 设置超时，避免无限等待
    setTimeout(() => {
      if (!scriptLoaded) {
        scriptLoading = false;
        const blackBox = getTongDunBlackBox();
        if (blackBox) {
          scriptLoaded = true;
          resolve(blackBox);
        } else {
          resolve(null);
        }
      }
    }, 10000); // 10秒超时
  });

  return scriptLoadPromise;
}

/**
 * 确保同盾脚本已加载并获取 black_box
 * 如果脚本未加载，会立即加载
 * @returns Promise<string | null> 返回 black_box 值
 */
export async function ensureTongDunLoaded(): Promise<string | null> {
  return initTongDunScript();
}

/**
 * 获取当前的 black_box 值（不触发加载）
 * @returns string | null
 */
export function getTongDunBlackBox(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (typeof window._fmOpt?.getinfo !== 'function') {
    return null;
  }
  return window._fmOpt.getinfo();
}

/**
 * 检查脚本是否已加载
 * @returns boolean
 */
export function isTongDunLoaded(): boolean {
  return scriptLoaded;
}

// 声明全局类型
declare global {
  interface Window {
    _fmOpt?: {
      partner: string;
      success: (data: string) => void;
      getinfo?: () => string;
    };
  }
}
